import React, { useState, useEffect, useCallback } from "react";
import "../../assets/styles/Testimonial.css";
const { testimonialData } = require("../../data/TestimonialData");

const isYouTube = (url) => !!url && (url.includes("youtube.com") || url.includes("youtu.be"));

const toYouTubeEmbed = (url) => {
  if (!url) return url;
  try {
    if (url.includes("watch?v=")) return url.replace("watch?v=", "embed/");
    if (url.includes("youtu.be/")) {
      const id = url.split("youtu.be/")[1].split(/[?&]/)[0];
      return `https://www.youtube.com/embed/${id}`;
    }
  } catch (e) {
    return url;
  }
  return url;
};

const Testimonial = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const total = Array.isArray(testimonialData) ? testimonialData.length : 0;

  useEffect(() => {
    if (currentIndex >= total && total > 0) setCurrentIndex(0);
  }, [total, currentIndex]);

  const handlePrev = useCallback(() => setCurrentIndex((i) => (i - 1 + total) % total), [total]);
  const handleNext = useCallback(() => setCurrentIndex((i) => (i + 1) % total), [total]);

  // autoplay
  useEffect(() => {
    if (!isPlaying || total <= 1) return;
    const t = setInterval(() => setCurrentIndex((i) => (i + 1) % total), 5000);
    return () => clearInterval(t);
  }, [isPlaying, total]);

  // keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") handlePrev();
      if (e.key === "ArrowRight") handleNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleNext, handlePrev]);

  if (!total) return null;

  return (
    <section id="testimonials" className="w-full py-16 bg-white">
      <div className="max-w-6xl mx-auto px-4">
        <header className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-extrabold text-sky-900">Trusted by teams, loved by partners</h2>
          <p className="text-sky-600 mt-2">Real feedback from organisations we partner with to deliver growth.</p>
        </header>

        <div className="relative" onMouseEnter={() => setIsPlaying(false)} onMouseLeave={() => setIsPlaying(true)}>
          {/* Slider viewport - ensures no overlap with content */}
          <div className="overflow-hidden rounded-2xl" style={{ background: 'linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)' }}>
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{ width: `${total * 100}%`, transform: `translateX(-${currentIndex * (100 / total)}%)` }}
            >
              {testimonialData.map((item) => {
                const isVideo = !!item.videoUrl;
                return (
                  <article key={item.id} className="flex-shrink-0 px-6 py-10 md:py-12" style={{ width: `${100 / total}%` }}>
                    <div className="bg-white shadow-lg border border-sky-50 rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-center gap-6">
                      <div className="w-28 h-28 md:w-32 md:h-32 rounded-full overflow-hidden flex-shrink-0 bg-sky-50 border-2 border-sky-100 flex items-center justify-center">
                        {isVideo ? (
                          isYouTube(item.videoUrl) ? (
                            <iframe
                              className="w-full h-full object-cover"
                              src={toYouTubeEmbed(item.videoUrl)}
                              title={item.name}
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          ) : (
                            <video className="w-full h-full object-cover" controls>
                              <source src={item.videoUrl} type="video/mp4" />
                              Your browser does not support the video tag.
                            </video>
                          )
                        ) : item.image ? (
                          <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="text-sky-300 text-3xl">👤</div>
                        )}
                      </div>

                      <div className="flex-1 text-center md:text-left">
                        <p className="text-lg md:text-xl text-sky-900 font-medium leading-relaxed">“{item.testimonial}”</p>
                        <div className="mt-4 flex flex-col md:flex-row md:items-center md:gap-4">
                          <span className="font-semibold text-sky-800">{item.name}</span>
                          {item.position && (
                            <span className="text-sky-500 hidden md:inline"> — {item.position}, {item.company}</span>
                          )}
                          {item.handle && (
                            <span className="text-sky-500">{item.handle.startsWith('http') ? (
                              <a href={item.handle} target="_blank" rel="noopener noreferrer" className="underline">{item.handle.replace(/^https?:\/\//, '')}</a>
                            ) : (
                              <span>@{item.handle}</span>
                            )}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>

          {/* Controls */}
          <div className="mt-6 flex items-center justify-center gap-4">
            <button
              onClick={handlePrev}
              aria-label="Previous"
              className="p-2 rounded-full bg-sky-50 text-sky-700 hover:bg-sky-100 shadow"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>

            <div className="flex items-center gap-2">
              {testimonialData.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  aria-label={`Go to testimonial ${idx + 1}`}
                  className={`w-3 h-3 rounded-full transition-all ${idx === currentIndex ? 'bg-sky-700 scale-110' : 'bg-sky-200'}`}
                />
              ))}
            </div>

            <button
              onClick={handleNext}
              aria-label="Next"
              className="p-2 rounded-full bg-sky-50 text-sky-700 hover:bg-sky-100 shadow"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonial;
